import { SortDescriptor } from '@progress/kendo-data-query/dist/npm/sort-descriptor';
import {
  Grid,
  GridColumn,
  GridColumnMenuCheckboxFilter,
  GridColumnMenuProps,
  GridColumnProps,
  GridColumnResizeEvent,
  GridCustomFilterCellProps,
  GridFilterChangeEvent,
  GridFilterOperators,
  GridProps
} from '@progress/kendo-react-grid';
import { SettingValueType } from '@Shared.Angular/@types/core/contracts/constants/settingValueTypes';
import { getService } from '@Shared.Angular/flowingly.hooks/useService';
import React, {
  Children,
  cloneElement,
  isValidElement,
  ReactElement,
  useEffect,
  useRef,
  useState
} from 'react';
import {
  adjustFlexibleColumns,
  calculateFixedColumnsWidth,
  distributeRemainingSpace,
  IColumnInfo,
  MIN_COL_WIDTH,
  parseStoredWidth,
  SIDEBAR_WIDTH
} from './KendoReactGrid.utils';

interface IKendoReactGridProps extends GridProps {
  id: string;
  manuallyFilteredFieldNames?: string[];
}

const DEBOUNCE_RESIZE = 500;
const DEBOUNCE_SAVE = 200;

/**
 * A wrapper around KendoReact Grid for client-side operations (paging, sorting, filtering).
 * - Columns with resizable={false} MUST have a width prop set
 * - Column widths are saved as percentages and applied responsively
 */
export const KendoReactGrid = (props: IKendoReactGridProps) => {
  const APP_CONFIG = getService('APP_CONFIG');
  const accountService = getService('accountApiService');
  const [skip, setSkip] = useState(0);
  const [gridWidth, setGridWidth] = useState(0);
  const gridRef = useRef<HTMLDivElement>(null);
  const saveTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const filterOperators = {
    text: [
      { text: 'grid.filterContainsOperator', operator: 'contains' },
      { text: 'grid.filterNotContainsOperator', operator: 'doesnotcontain' },
      { text: 'grid.filterEqOperator', operator: 'eq' },
      { text: 'grid.filterNotEqOperator', operator: 'neq' },
      { text: 'grid.filterIsEmptyOperator', operator: 'isempty' },
      { text: 'grid.filterIsNotEmptyOperator', operator: 'isnotempty' }
    ],
    date: [
      { text: 'grid.filterAfterOperator', operator: 'gt' },
      { text: 'grid.filterBeforeOperator', operator: 'lt' }
    ]
  };

  const getGridWidth = (): number =>
    gridRef.current?.offsetWidth || window.innerWidth - SIDEBAR_WIDTH;

  const getGridConfigs = () => {
    try {
      return JSON.parse(APP_CONFIG.userGridConfigs);
    } catch {
      return {};
    }
  };

  const getSavedWidths = () => getGridConfigs()[props.id]?.widths || {};

  const persistGridConfig = (field: string, widthPercentage: string) => {
    const gridConfigs = getGridConfigs();
    gridConfigs[props.id] = {
      ...gridConfigs[props.id],
      widths: {
        ...gridConfigs[props.id]?.widths,
        [field]: widthPercentage
      }
    };

    accountService.changeUserSetting(
      'UserGridConfigs',
      JSON.stringify(gridConfigs),
      SettingValueType.string,
      true
    );
  };

  const calculateWidthPercentage = (newWidth: number): string => {
    const currentGridWidth = getGridWidth();
    const fixedWidth = calculateFixedColumnsWidth(props.children);
    const flexibleSpace = currentGridWidth - fixedWidth;
    return ((newWidth / flexibleSpace) * 100).toFixed(2);
  };

  // Update grid width on mount and window resize
  useEffect(() => {
    const updateWidth = () => setGridWidth(getGridWidth());
    updateWidth();

    let timeout: ReturnType<typeof setTimeout>;
    const handleResize = () => {
      clearTimeout(timeout);
      timeout = setTimeout(updateWidth, DEBOUNCE_RESIZE);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeout);
    };
  }, []);

  const saveColumnWidths = (event: GridColumnResizeEvent) => {
    clearTimeout(saveTimeout.current);
    saveTimeout.current = setTimeout(() => {
      const column = event.columns[event.index];
      if (!column.field) return;

      const percentage = calculateWidthPercentage(event.newWidth);
      persistGridConfig(column.field, `${percentage}%`);
    }, DEBOUNCE_SAVE);
  };

  const buildColumnWidths = (): Map<string, number> => {
    const savedWidths = getSavedWidths();
    const currentGridWidth = gridWidth || getGridWidth();
    const fixedWidth = calculateFixedColumnsWidth(props.children);
    const flexibleSpace = currentGridWidth - fixedWidth;

    let columns: IColumnInfo[] = [];
    const columnsWithoutWidth: string[] = [];
    let columnIndex = 0;

    // Parse all columns and categorize them
    Children.forEach(props.children, (child: ReactElement<GridColumnProps>) => {
      if (!isValidElement(child)) return;

      const field = child.props.field || `__column_${columnIndex++}__`;
      const defaultWidth = Number(child.props.width);
      const isFixed = child.props.resizable === false;

      if (isFixed && defaultWidth) {
        columns.push({ field, width: defaultWidth, isFixed: true });
      } else {
        const width = parseStoredWidth(
          savedWidths[field],
          defaultWidth,
          flexibleSpace
        );
        width
          ? columns.push({ field, width, isFixed: false })
          : columnsWithoutWidth.push(field);
      }
    });

    // Apply width distribution and adjustments
    columns = distributeRemainingSpace(
      columns,
      columnsWithoutWidth,
      currentGridWidth
    );
    columns = adjustFlexibleColumns(
      columns,
      currentGridWidth,
      columnsWithoutWidth.length > 0
    );

    return new Map(
      columns.map((column) => [
        column.field,
        Math.max(column.width, MIN_COL_WIDTH)
      ])
    );
  };

  const columnWidths = buildColumnWidths();

  const modifiedChildren = Children.map(
    props.children,
    (child: ReactElement<GridColumnProps>, index) => {
      if (!isValidElement(child)) return child;
      const field = child.props.field || `__column_${index}__`;
      const width = columnWidths.get(field);
      return width ? cloneElement(child, { ...child.props, width }) : child;
    }
  );

  return (
    <div className="kendo-react-grid" ref={gridRef}>
      <link
        href="https://kendo.cdn.telerik.com/themes/7.0.1/material/material-main.css"
        rel="stylesheet"
        type="text/css"
      />
      <Grid
        key={`grid-${props.id}-${gridWidth}`} // Force re-render on resize
        {...props}
        filterOperators={filterOperators}
        onFilterChange={(x) => {
          setSkip(0);
          if (props.manuallyFilteredFieldNames?.length && x.filter?.filters) {
            x.filter.filters = x.filter.filters.filter(
              (f) =>
                !props.manuallyFilteredFieldNames.some(
                  (mff) => mff === (f as any).field
                )
            );
          }
          props.onFilterChange?.(x);
        }}
        onPageChange={(e) => setSkip(e.page.skip)}
        skip={skip}
        onColumnResize={saveColumnWidths}
      >
        {modifiedChildren}
      </Grid>
    </div>
  );
};

export {
  GridColumn,
  GridColumnMenuProps,
  GridColumnMenuCheckboxFilter,
  GridCustomFilterCellProps,
  GridFilterChangeEvent,
  GridFilterOperators,
  SortDescriptor
};
